<template>
  <main id="home" class="guests-page" v-if="$route.path === '/home'">
    <div class="guests-page__head">
      <h1 class="guests-page__title">Dashboard VillaOS</h1>
      <div class="home-quick-actions">
        <router-link to="/facilities" class="villa-btn villa-btn--primary">
          <i class="fa-solid fa-building"></i> Strutture
        </router-link>
        <router-link to="/guests" class="villa-btn villa-btn--ghost">
          <i class="fa-solid fa-users"></i> Archivio ospiti
        </router-link>
        <button class="villa-btn villa-btn--ghost" disabled title="In arrivo">
          <i class="fa-solid fa-file-invoice"></i> Fatturazione
          <small class="home-quick-actions__soon">presto</small>
        </button>
      </div>
    </div>

    <!-- BLOCCO 1: Stato operativo -->
    <h2 class="guests-page__title dash-block-title">Stato operativo</h2>
    <div class="dash-insights">
      <div class="dash-card">
        <div class="dash-card__value">{{ summary.activeFacilitiesCount }}</div>
        <div class="dash-card__label">Strutture attive</div>
      </div>
      <div class="dash-card">
        <div class="dash-card__value">{{ summary.activeGuestsCount }}</div>
        <div class="dash-card__label">Ospiti presenti</div>
      </div>
      <div class="dash-card">
        <div class="dash-card__value">
          {{ summary.occupiedBeds }} / {{ summary.totalBeds }}
        </div>
        <div class="dash-card__label">Posti occupati</div>
        <div class="dash-card__hint">
          {{ summary.globalOccupancyRate }}% occupazione globale
        </div>
      </div>
      <div class="dash-card">
        <div class="dash-card__value">{{ summary.totalMaxCapacity }}</div>
        <div class="dash-card__label">Capienza totale</div>
      </div>
    </div>

    <!-- BLOCCO 2: Economia (solo dev/admin, D10A) -->
    <template v-if="canViewEconomics">
      <h2 class="guests-page__title dash-block-title">Economia</h2>
      <div class="dash-insights">
        <div class="dash-card">
          <div class="dash-card__value">
            {{ euro(summary.currentMonthlyRevenueTotal) }}
          </div>
          <div class="dash-card__label">Rendita mensile attuale</div>
        </div>
        <div class="dash-card">
          <div class="dash-card__value">
            {{ euro(summary.potentialMonthlyRevenueTotal) }}
          </div>
          <div class="dash-card__label">Rendita mensile potenziale</div>
        </div>
        <div class="dash-card">
          <div class="dash-card__value">{{ euro(summary.revenueGap) }}</div>
          <div class="dash-card__label">Gap potenziale</div>
        </div>
        <div class="dash-card">
          <div class="dash-card__value">
            {{ euro(summary.averageMonthlyFeeGlobal) }}
          </div>
          <div class="dash-card__label">Retta media reale globale</div>
        </div>
        <div class="dash-card">
          <div class="dash-card__value">{{ summary.payingGuestsCount }}</div>
          <div class="dash-card__label">Ospiti con retta valorizzata</div>
        </div>
      </div>
    </template>

    <!-- BLOCCO 3: Avvisi (solo se presenti, conteggi reali) -->
    <template v-if="hasAlerts">
      <h2 class="guests-page__title dash-block-title">Avvisi</h2>
      <div class="dash-alerts">
        <div v-if="summary.facilitiesWithoutExpectedFee > 0" class="dash-alert">
          <i class="fa-solid fa-triangle-exclamation"></i>
          {{ summary.facilitiesWithoutExpectedFee }} struttur{{
            summary.facilitiesWithoutExpectedFee === 1 ? "a" : "e"
          }}
          senza retta media attesa
        </div>
        <div v-if="summary.guestsWithoutMonthlyFee > 0" class="dash-alert">
          <i class="fa-solid fa-triangle-exclamation"></i>
          {{ summary.guestsWithoutMonthlyFee }} ospit{{
            summary.guestsWithoutMonthlyFee === 1 ? "e" : "i"
          }}
          senza retta mensile
        </div>
        <div v-if="summary.roomsWithoutBeds > 0" class="dash-alert">
          <i class="fa-solid fa-triangle-exclamation"></i>
          {{ summary.roomsWithoutBeds }} camer{{
            summary.roomsWithoutBeds === 1 ? "a" : "e"
          }}
          senza posti letto
        </div>
      </div>
    </template>

    <!-- BLOCCO 4: Strutture -->
    <FacilitiesPanel @changed="loadSummary" />
  </main>
  <router-view></router-view>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import FacilitiesPanel from "@/components/FacilitiesPanel.vue";

export default {
  name: "HomePage",
  components: { FacilitiesPanel },
  data() {
    return {
      summary: {
        facilitiesCount: 0,
        activeFacilitiesCount: 0,
        totalMaxCapacity: 0,
        guestsCount: 0,
        activeGuestsCount: 0,
        roomsCount: 0,
        bedsCount: 0,
        totalBeds: 0,
        occupiedBeds: 0,
        availableBeds: 0,
        globalOccupancyRate: 0,
        // Metriche economiche dinamiche (D6C-FIX)
        currentMonthlyRevenueTotal: 0,
        potentialMonthlyRevenueTotal: 0,
        averageMonthlyFeeGlobal: 0,
        revenueGap: 0,
        payingGuestsCount: 0,
        // Alert (D6D)
        facilitiesWithoutExpectedFee: 0,
        guestsWithoutMonthlyFee: 0,
        roomsWithoutBeds: 0,
      },
    };
  },
  computed: {
    ...mapGetters(["canViewEconomics"]),
    hasAlerts() {
      return (
        this.summary.facilitiesWithoutExpectedFee > 0 ||
        this.summary.guestsWithoutMonthlyFee > 0 ||
        this.summary.roomsWithoutBeds > 0
      );
    },
  },
  methods: {
    ...mapActions(["fetchDashboardSummary"]),
    euro(v) {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return `€ ${n.toLocaleString("it-IT", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })}`;
    },
    async loadSummary() {
      try {
        this.summary = await this.fetchDashboardSummary();
      } catch {
        // insight non bloccanti: in caso di errore restano a 0
      }
    },
  },
  mounted() {
    const { firstLogin } = this.$route.query || {};
    if (firstLogin === "1") {
      this.$toast.success(
        "Benvenuto! Abbiamo inviato una password iniziale al tuo indirizzo email"
      );
      const q = { ...this.$route.query };
      delete q.firstLogin;
      this.$router.replace({ path: this.$route.path, query: q });
    }
    this.$toast.clear();
    this.loadSummary();
  },
};
</script>

<style src="@/css/GuestsPage.css" />
<style src="@/css/AppLayout.css" />
